import React, { useState, useEffect, useCallback } from "react";
import { FiThumbsUp } from "react-icons/fi";
import { BiErrorCircle } from "react-icons/bi";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table
} from "react-bootstrap";
import {
  applyLicenseKey,
  extractLicenseKey,
  getLicenseStatus,
  isLicenseActive
} from './api';
import { INK } from 'Utils/styleTokens';

const FEATURE_LABELS = {
  'masking': 'Masking',
  'encryption': 'Encryption',
  'discover': 'Discover',
  'manage': 'Manage',
  'privacy-platform': 'Privacy Platform',
  'integration': 'Integration',
  'custom-db': 'Custom database engines'
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US');
};

const LicensePanel = (props) => {
  const [license, setLicense] = useState(null);
  const [licenseError, setLicenseError] = useState(null);
  const [licenseInput, setLicenseInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [copyFlash, setCopyFlash] = useState(false);

  const loadLicense = useCallback(async () => {
    try {
      setLicenseError(null);
      const resp = await getLicenseStatus();
      setLicense(resp || null);
    } catch (e) {
      console.log('license load failed', e);
      setLicenseError(e?.message || 'Failed to load license status.');
      setLicense(null);
    }
  }, []);

  useEffect(() => {
    loadLicense();
  }, [loadLicense]);

  const onLicenseFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setLicenseInput(text);
    } catch (err) {
      setLicenseError('Could not read file: ' + (err?.message || err));
    } finally {
      e.target.value = '';
    }
  };

  const onApplyLicense = async () => {
    const key = extractLicenseKey(licenseInput);
    if (!key) {
      setLicenseError('Paste a license key or choose a license file first.');
      return;
    }
    setUploading(true);
    setLicenseError(null);
    setUploadSuccess(false);
    try {
      const resp = await applyLicenseKey(key);
      if (resp && resp.features) {
        setLicense(resp);
        setUploadSuccess(true);
        setLicenseInput('');
      } else {
        setLicenseError('Invalid license key. The server rejected it.');
      }
    } catch (err) {
      setLicenseError(err?.message || 'Failed to apply license.');
    } finally {
      setUploading(false);
    }
  };

  const copyTenantId = async () => {
    if (!license?.tenantId) return;
    try {
      await navigator.clipboard.writeText(license.tenantId);
      setCopyFlash(true);
      setTimeout(() => setCopyFlash(false), 1500);
    } catch (_) {
      // Clipboard blocked — ignore.
    }
  };

  const active = isLicenseActive(license);
  const sourceLabel =
    license?.source === 'AUTO' ? 'Trial'
    : license?.source === 'MANUAL' ? 'Customer License'
    : license?.source === 'NONE' ? 'None' : (license?.source || '—');

  const features = license?.features || {};
  const platformFeature = features['privacy-platform'] || {};
  const featureKeys = Object.keys(FEATURE_LABELS).filter((k) => k !== 'privacy-platform');

  return (
    <Card>
      <div className='entity-panel-table-header d-flex'>
        <div className='text-left widget-header p-3'>
          <span>License</span>
        </div>
      </div>
      <Card.Body>
        <Row>
          <Col className='pl-4 pr-4'>
            <div className='d-flex justify-content-end align-items-center mb-2'>
              {license && (
                <Badge variant={active ? 'success' : 'danger'}>
                  {active ? 'Active' : 'Inactive'}
                </Badge>
              )}
            </div>

            {licenseError && (
              <Alert variant='danger' className='py-2'>
                <BiErrorCircle className='mr-2' />
                {licenseError}
              </Alert>
            )}
            {uploadSuccess && (
              <Alert variant='success' className='py-2'>
                <FiThumbsUp className='mr-2' />
                License applied successfully.
              </Alert>
            )}

            {!license ? (
              <p className='text-muted' style={{ fontSize: '12px' }}>Loading license…</p>
            ) : (
              <>
                {!active && (
                  <Alert variant='warning' className='py-2' style={{ fontSize: '12px' }}>
                    <strong>License needed.</strong> No licensed features are currently active.
                    Send the Tenant ID below to C<sup>2</sup> Data to request a license, then paste or
                    upload the returned license key below.
                  </Alert>
                )}

                <Table size='sm' borderless className='mb-3' style={{ fontSize: '12px' }}>
                  <tbody>
                    <tr>
                      <td style={{ width: '150px' }}><strong>Tenant ID</strong></td>
                      <td>
                        <code>{license.tenantId || '—'}</code>
                        {license.tenantId && (
                          <Button
                            variant='link'
                            size='sm'
                            className='p-0 ml-2'
                            onClick={copyTenantId}
                          >
                            {copyFlash ? 'Copied' : 'Copy'}
                          </Button>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Source</strong></td>
                      <td>{sourceLabel}</td>
                    </tr>
                    <tr>
                      <td><strong>Applied</strong></td>
                      <td>{formatDate(license.appliedAt)}</td>
                    </tr>
                    {props.version && (
                      <tr>
                        <td><strong>Version</strong></td>
                        <td>{props.version.replace(/^v/, '')}</td>
                      </tr>
                    )}
                    <tr>
                      <td><strong>{FEATURE_LABELS['privacy-platform']}</strong></td>
                      <td>
                        <span className='mr-2'>{formatDate(platformFeature.expiresAt)}</span>
                        <Badge variant={platformFeature.valid ? 'success' : 'secondary'}>
                          {platformFeature.valid
                            ? 'Valid'
                            : (platformFeature.expiresAt ? 'Expired' : 'Not Licensed')}
                        </Badge>
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <Table size='sm' striped className='mb-4' style={{ fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Expires</th>
                      <th className='text-right'>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featureKeys.map((key) => {
                      const f = features[key] || {};
                      return (
                        <tr key={key}>
                          <td>{FEATURE_LABELS[key]}</td>
                          <td>{formatDate(f.expiresAt)}</td>
                          <td className='text-right'>
                            <Badge variant={f.valid ? 'success' : 'secondary'}>
                              {f.valid ? 'Valid' : (f.expiresAt ? 'Expired' : 'Not Licensed')}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </>
            )}

            <div className='p-3' style={{ background: INK[50], borderRadius: '10px' }}>
              <h6 className='mb-2'>Apply a new license</h6>
              <p className='text-muted mb-3' style={{ fontSize: '12px' }}>
                Upload the license file you received from C<sup>2</sup> Data, or paste its
                contents (either the raw JWT or a JSON object with a <code>licenseKey</code> field) below.
              </p>
              <Form.Group>
                <Form.File
                  id='license-file-upload'
                  label='Choose license file'
                  custom
                  data-browse='Browse'
                  accept='.txt,.json,.jwt,.license,application/json,text/plain'
                  onChange={onLicenseFile}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontSize: '12px' }}>Or paste license key / JSON</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={4}
                  value={licenseInput}
                  onChange={(e) => setLicenseInput(e.target.value)}
                  placeholder='eyJhbGciOi...   or   { "licenseKey": "eyJhbGciOi..." }'
                  style={{ fontFamily: 'monospace', fontSize: '11px' }}
                />
              </Form.Group>
              <div className='d-flex'>
                <Button
                  variant='primary'
                  disabled={uploading || !licenseInput.trim()}
                  onClick={onApplyLicense}
                >
                  {uploading && (
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                      className='mr-2'
                    />
                  )}
                  {uploading ? 'Applying…' : 'Apply License'}
                </Button>
                {licenseInput && (
                  <Button
                    variant='outline-secondary'
                    className='ml-2'
                    onClick={() => { setLicenseInput(''); setLicenseError(null); setUploadSuccess(false); }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default LicensePanel;
